<template>
  <div>
    <job-filters />
    <base-form :title="$t('job.create.title')"
               :show-footer="false">
      <wizard :vertical="isMobile"
              :start-index.sync="activeTabIndex"
              :loading="loading"
              ref="wizard"
              @on-complete="onComplete">
        <wizard-tab :before-change="() => validate('step1')">
        <span slot="title">
          <i class="fas fa-laptop"></i> {{$t('job.create.product.title')}}
        </span>
          <job-create-product ref="step1"
                              :products="jobPostingProducts"
                              :job="form.job"
                              :client="form.client"
                              :product="form.product"
                              @product-select="onProductSelect"
                              @job-board-select="onJobBoardSelect"
                              @payment-selected="onPaymentSelected"
                              @segment-change="onSegmentsChange"
                              @client-change="onClientChange"
                              @po_number-change="onPoNumberChange"
                              @contracts-balance="onContractsBalance">
          </job-create-product>
        </wizard-tab>
        <wizard-tab :before-change="() => validate('step2')">
        <span slot="title">
          <i class="fas fa-info"></i> {{$t('job.create.details.title')}}
        </span>
          <job-create-details ref="step2"
                              class="w-full"
                              :client="form.client"
                              :job="form.job"
                              :product="form.product"
                              :user="user"
                              @on-validated="syncJob">
          </job-create-details>
        </wizard-tab>
        <wizard-tab :before-change="() => validate('step3')">
        <span slot="title">
          <i class="fas fa-align-left"></i> {{$t('job.create.segments.title')}}
        </span>
          <job-create-segments class="w-full"
                               :segments="jobSegments"
                               :extra-info="form.job"
                               @on-validated="syncSegments"
                               ref="step3"></job-create-segments>
        </wizard-tab>
        <wizard-tab v-if="false">
        <span slot="title">
          <i class="fas fa-upload"></i> {{$t('job.create.publish.title')}}
        </span>
          <job-create-publish class="w-full"
                              :job="form.job"
                              :product="form.product"
                              :payment="form.payment"
                              :job-boards="form.jobBoards"
                              :contracts-balance="contractsBalance"
                              :price-data="jobPrice"
                              :client="form.client">
          </job-create-publish>
        </wizard-tab>
      </wizard>
    </base-form>
  </div>
</template>
<script>
  import clone from 'lodash/cloneDeep'
  import { Wizard, WizardTab } from 'src/components'
  import JobCreateProduct from './JobCreate/JobCreateProduct'
  import JobCreateSegments from './JobCreate/JobCreateSegments'
  import JobCreatePublish from './JobCreate/JobCreatePublish'
  import JobCreateDetails from './JobCreate/JobCreateDetails'
  import JobService from 'src/services/JobService'
  import JobFilters from './JobFilters';
  import { mapGetters, mapActions, mapState } from 'vuex'
  import { DAY, DEFAULT_DATE_FORMAT } from 'src/config/dateUtils'
  import ClientService from 'src/services/ClientService'
  import emptyJobForm from './emptyJobForm'

  let isDev = process.env.NODE_ENV === 'development'
  export default {
    name: 'job-create',
    components: {
      JobCreateDetails,
      Wizard,
      WizardTab,
      JobCreateProduct,
      JobCreateSegments,
      JobCreatePublish,
      JobFilters
    },
    data () {
      const form = clone(emptyJobForm)
      const jobToClone = this.$store.state.jobs.jobToClone
      if (this.$route.query.clone && jobToClone) {
        form.job = clone(jobToClone)
        form.client = clone(jobToClone.client)
        form.client_id = jobToClone.client.uuid
        form.jobBoards = jobToClone.job_boards
      }
      return {
        form: form,
        clientValid: true,
        loading: false,
        jobPrice: {},
        jobSegments: [],
        contractsBalance: [],
        activeTabIndex: 0,
        formSaved: false
      }
    },
    computed: {
      ...mapGetters('utils', ['isMobile']),
      ...mapGetters('config', ['jobPostingProducts', 'allProducts']),
      ...mapState('user', {
        user: state => state.user.user
      }),
      ...mapState({
        currentClient: state => state.client.currentClient
      })
    },
    methods: {
      ...mapActions('config', ['getConfig']),
      async validate (step) {
        let stepRef = this.$refs[step]
        if (stepRef.validate) {
          return stepRef.validate()
        }
        return Promise.resolve(true)
      },
      async getClientCredits (client) {
        let {data} = await ClientService.getClientCredits(client.uuid)
        return data.credits
      },
      async onClientChange (client) {
        let selectedClient = client
        if (!client.uuid) {
          this.form.client = {
            name: client.name
          }
          // create the client
          let {data} = await ClientService.create(client)
          selectedClient = data
        }
        let credits = await this.getClientCredits(selectedClient)
        this.form.client = {
          ...selectedClient,
          credits
        }
        this.clientValid = client.name !== ''
      },
      onPoNumberChange(value) {
        this.form.job.po_number = value
      },
      syncJob (newJob) {
        this.form.job = newJob
        if (newJob.text_type === 'own') {
          this.form.job.text_html = newJob.text_own
        }
      },
      onProductSelect (product) {
        this.form.product = product
        this.form.job.from = new Date()
        this.form.job.till = new Date(new Date().getTime() + product.job_days * DAY)

      },
      onJobBoardSelect (jobBoards) {
        this.form.jobBoards = jobBoards
      },
      onSegmentsChange (segments) {
        this.jobSegments = segments
      },
      onPaymentSelected(payment) {
        this.form.payment = payment
      },
      onContractsBalance(value) {
        this.contractsBalance = value
      },
      syncSegments (segments) {
        this.form.job = {
          ...this.form.job,
          ...segments
        }
      },
      async onComplete () {
        try {
          this.loading = true
          let jobBoards = []
          this.form.jobBoards.forEach(jobBoardValue => {
            jobBoards.push(jobBoardValue.id)
          })

          let job = {
            ...this.form.job,
            job_boards: jobBoards,
            client_id: this.form.client.uuid,
            product_id: this.form.product.product_id,
            from: this.$formatDate(this.form.job.from, DEFAULT_DATE_FORMAT),
            till: this.$formatDate(this.form.job.till, DEFAULT_DATE_FORMAT)
          }
          let result = await JobService.create(job);
          this.formSaved = true
          this.$router.push(`/jobs/${result.data.uuid}`);
        } finally {
          this.loading = false
        }
      }
    },
    async beforeRouteLeave(to, from, next) {
      if (this.formSaved) {
        return next()
      }
      try {
        let result = confirm(this.$t('job.create.saveConfirm'))
        next(result)
      } catch (e) {
        next(false)
      }
    },
    async created () {
      this.getConfig()
      let { clientId, productId }  = this.$route.query
      if (productId) {
        let product = this.allProducts.find(product => product.product_id.toString() === productId.toString())
        if (product) {
          this.form.product = product
        }
      }
      if (clientId) {
        if (this.currentClient && this.currentClient.uuid) {
          this.form.client = this.currentClient
        } else {
          let { data } = await ClientService.getById(clientId)
          this.form.client = data
        }
      }
    }
  }
</script>
<style>
</style>
